import {
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  Select,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';
import { FormikProps } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { ReactNode } from 'react';

interface IFormikSelect<T> {
  children: ReactNode;
  formik: FormikProps<any>;
  name: string;
  label?: string;
  type?: string;
  helperText?: string;
  onChange?: (
    event: SelectChangeEvent<T> | React.ChangeEvent<any>,
    child?: ReactNode | object
  ) => void;
}

export const FormikSelect = <T extends string>({
  children,
  formik,
  name,
  label,
  type,
  className,
  color, // FormControlProps
  disabled, // FormControlProps
  fullWidth, // FormControlProps
  margin, // FormControlProps
  required, // FormControlProps
  variant, // FormControlProps
  onChange,
  dataPup,
  ...rest
}: IFormikSelect<T> &
  (FormControlProps | SelectProps<T>) & { dataPup?: string }) => {
  const formikProps = formik.getFieldProps(name);

  /**
   * Chain onChange to caller onChange
   * @param event HTML change event
   */
  const onChangeHandler = (
    event: SelectChangeEvent<T> | React.ChangeEvent<any>,
    child?: ReactNode | object
  ) => {
    if (onChange) {
      onChange(event, child);
    }
    formikProps.onChange(event);
  };

  return (
    <FormControl
      className={className}
      color={color}
      disabled={disabled}
      fullWidth={fullWidth}
      margin={margin}
      required={required}
      variant={variant}
      error={!!get(formik.touched, name) && !!get(formik.errors, name)}
      size="small"
    >
      <InputLabel>{label}</InputLabel>
      <Select
        inputProps={{
          'data-pup': dataPup ?? undefined,
        }}
        {...formikProps}
        {...(rest as SelectProps<T>)}
        label={label}
        name={name}
        onChange={onChangeHandler}
      >
        {children}
      </Select>
      {get(formik.touched, name) && get(formik.errors, name) && (
        <FormHelperText>{get(formik.errors, name)}</FormHelperText>
      )}
    </FormControl>
  );
};

FormikSelect.propTypes = {
  children: PropTypes.any.isRequired,
  formik: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  type: PropTypes.string,
  helperText: PropTypes.string,
  onChange: PropTypes.func,
};
